import React, { useEffect, useState } from 'react';
import { Spinner } from '@nextui-org/react';
import { Clock, MapPin, Navigation, Search, X } from 'lucide-react';
import searchService, { DiscoverSections, SearchResult } from '../../services/searchService';
import { formatDistance, GeoPoint, NearbyItem } from '../../services/nearbyService';
import ThumbnailImage from '../ThumbnailImage';

const ACCENT = '#3fced1';

const scrollRowClass =
  'flex gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * Gefüllter Empty-State des Such-Explorers: Zuletzt gesucht (Chips) +
 * horizontale Slider (Profis, News, Turniere, Nearby, Neue Spieler).
 * Lädt die Discover-Sektionen selbst; Nearby kommt als Props vom Modal
 * (Standort-Logik bleibt dort). Reihenfolge = Marcels Vorgabe (2026-09-01).
 */
interface SearchDiscoverContentProps {
  onNavigate: (link: string) => void;
  onSearch: (query: string) => void;
  nearbyLocation: GeoPoint | null;
  nearbyItems: NearbyItem[] | null;
  nearbyLoading: boolean;
  geoLoading: boolean;
  geoError: string | null;
  onRequestLocation: () => void;
}

const SearchDiscoverContent: React.FC<SearchDiscoverContentProps> = ({
  onNavigate,
  onSearch,
  nearbyLocation,
  nearbyItems,
  nearbyLoading,
  geoLoading,
  geoError,
  onRequestLocation,
}) => {
  const [sections, setSections] = useState<DiscoverSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState<string[]>(() => searchService.getRecentSearches());

  useEffect(() => {
    let cancelled = false;
    searchService
      .getDiscover()
      .then((data) => {
        if (!cancelled) setSections(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearRecent = () => {
    searchService.clearRecentSearches();
    setRecent([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner color="primary" />
      </div>
    );
  }

  const hasAnything =
    recent.length > 0 ||
    (sections &&
      (sections.proPlayers.length > 0 ||
        sections.news.length > 0 ||
        sections.tournaments.length > 0 ||
        sections.players.length > 0));

  if (!hasAnything && !nearbyLocation) {
    // Fallback: alter Empty-State, falls Discover nicht erreichbar ist
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Search size={56} className="mb-4 text-zinc-700" />
        <p className="max-w-xs text-base text-gray-500">
          Suche nach News, Turnieren, Vereinen, Spielstätten…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Zuletzt gesucht */}
      {recent.length > 0 && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <SectionHeading icon={<Clock size={15} style={{ color: ACCENT }} />} label="Zuletzt gesucht" />
            <button
              type="button"
              onClick={clearRecent}
              aria-label="Suchverlauf löschen"
              className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              <X size={12} />
              Löschen
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recent.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => onSearch(term)}
                className="rounded-full border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs font-semibold text-gray-300 transition-colors hover:border-[#3fced1]/50 hover:text-white"
              >
                {term}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 2. Beliebte Profis (runde Avatare) */}
      {sections && sections.proPlayers.length > 0 && (
        <section>
          <SectionHeading label="🎯 Beliebte Profis" className="mb-3" />
          <div className={scrollRowClass}>
            {sections.proPlayers.map((player) => (
              <button
                key={player.id}
                type="button"
                onClick={() => player.link && onNavigate(player.link)}
                className="w-[76px] shrink-0 text-center"
              >
                <AvatarCircle image={player.imageUrl} name={player.title} />
                <p className="mt-1.5 line-clamp-1 text-xs font-semibold text-gray-300">
                  {player.title}
                </p>
                {player.subtitle && (
                  <p className="line-clamp-1 text-[10px] text-gray-500">
                    {player.subtitle.replace('Weltrangliste ', '')}
                  </p>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 3. Aktuelle News (Bild-Cards) */}
      {sections && sections.news.length > 0 && (
        <section>
          <SectionHeading label="📰 Aktuelle News" className="mb-3" />
          <div className={scrollRowClass}>
            {sections.news.map((article) => (
              <DiscoverCard key={article.id} item={article} onNavigate={onNavigate} wide />
            ))}
          </div>
        </section>
      )}

      {/* 4. Kommende Turniere */}
      {sections && sections.tournaments.length > 0 && (
        <section>
          <SectionHeading label="🏆 Kommende Turniere" className="mb-3" />
          <div className={scrollRowClass}>
            {sections.tournaments.map((tournament) => (
              <DiscoverCard key={tournament.id} item={tournament} onNavigate={onNavigate} />
            ))}
          </div>
        </section>
      )}

      {/* 5. In deiner Nähe */}
      <section>
        <SectionHeading
          icon={<MapPin size={15} style={{ color: ACCENT }} />}
          label={`In deiner Nähe${nearbyLocation?.label ? ` · ${nearbyLocation.label}` : ''}`}
          className="mb-3"
        />
        {nearbyLocation ? (
          nearbyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner color="primary" size="sm" />
            </div>
          ) : nearbyItems && nearbyItems.length > 0 ? (
            <div className={scrollRowClass}>
              {nearbyItems.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  onClick={() => item.link && onNavigate(item.link)}
                  className="w-[180px] shrink-0 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-800/40 text-left transition-colors hover:bg-zinc-800"
                >
                  <CardImage image={item.image} alt={item.name || ''} />
                  <div className="p-2.5">
                    <p className="line-clamp-1 text-xs font-semibold text-white">{item.name || '–'}</p>
                    <p className="line-clamp-1 text-[11px] text-gray-500">
                      {item.typeLabel}
                      {item.distance != null ? ` · ${formatDistance(item.distance)}` : ''}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500">
              Keine Einträge in deiner Nähe gefunden.
            </p>
          )
        ) : (
          <div>
            <button
              type="button"
              onClick={onRequestLocation}
              disabled={geoLoading}
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800/60 px-4 py-2.5 text-sm font-semibold text-gray-200 transition-colors hover:bg-zinc-800 disabled:opacity-60"
            >
              {geoLoading ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <Navigation size={15} style={{ color: ACCENT }} />
              )}
              Ergebnisse in meiner Nähe anzeigen
            </button>
            {geoError && <p className="mt-2 text-xs text-red-400">{geoError}</p>}
          </div>
        )}
      </section>

      {/* 6. Neue Spieler (Community) */}
      {sections && sections.players.length > 0 && (
        <section>
          <SectionHeading label="👥 Neue Spieler" className="mb-3" />
          <div className={scrollRowClass}>
            {sections.players.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => profile.link && onNavigate(profile.link)}
                className="w-[76px] shrink-0 text-center"
              >
                <AvatarCircle image={profile.imageUrl} name={profile.title} />
                <p className="mt-1.5 line-clamp-1 text-xs font-semibold text-gray-300">
                  {profile.title}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const SectionHeading: React.FC<{ label: string; icon?: React.ReactNode; className?: string }> = ({
  label,
  icon,
  className,
}) => (
  <h3 className={`flex min-w-0 items-center gap-1.5 text-sm font-semibold text-gray-400 ${className ?? ''}`}>
    {icon}
    <span className="min-w-0 truncate">{label}</span>
  </h3>
);

const AvatarCircle: React.FC<{ image?: string | null; name: string }> = ({ image, name }) => {
  const [imgError, setImgError] = useState(false);
  if (image && !imgError) {
    return (
      // 56px-Avatar → 100er-Thumbnail (externe Profi-Bilder unverändert)
      <ThumbnailImage
        src={image}
        thumbSize={100}
        alt={name}
        onError={() => setImgError(true)}
        className="mx-auto h-14 w-14 rounded-full border border-zinc-700 object-cover"
      />
    );
  }
  return (
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-zinc-700 bg-zinc-800 text-sm font-bold text-gray-400">
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
};

const CardImage: React.FC<{ image?: string | null; alt: string }> = ({ image, alt }) => {
  const [imgError, setImgError] = useState(false);
  if (image && !imgError) {
    return (
      // 180-220px-Karten → 400er-Thumbnail statt Original-Poster
      <ThumbnailImage
        src={image}
        thumbSize={400}
        alt={alt}
        onError={() => setImgError(true)}
        className="aspect-video w-full object-cover"
      />
    );
  }
  return (
    <div className="flex aspect-video w-full items-center justify-center bg-zinc-800" style={{ color: ACCENT }}>
      <Search size={20} />
    </div>
  );
};

const DiscoverCard: React.FC<{
  item: SearchResult;
  onNavigate: (link: string) => void;
  wide?: boolean;
}> = ({ item, onNavigate, wide }) => (
  <button
    type="button"
    onClick={() => item.link && onNavigate(item.link)}
    className={`${wide ? 'w-[220px]' : 'w-[180px]'} shrink-0 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-800/40 text-left transition-colors hover:bg-zinc-800`}
  >
    <CardImage image={item.imageUrl} alt={item.title} />
    <div className="p-2.5">
      <p className="line-clamp-2 text-xs font-semibold leading-snug text-white">{item.title}</p>
      {item.subtitle && (
        <p className="mt-0.5 line-clamp-1 text-[11px] text-gray-500">{item.subtitle}</p>
      )}
    </div>
  </button>
);

export default SearchDiscoverContent;
